import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { vehicleAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Badge, Button, Spinner, Alert } from 'react-bootstrap';
import {
  FiTruck, FiCheckCircle, FiPauseCircle, FiUploadCloud,
  FiPlus, FiList, FiUsers, FiCopy, FiExternalLink, FiEdit2,
} from 'react-icons/fi';

const vehicleLink = (plateNumber: string) =>
  `${window.location.origin}/car?plate=${plateNumber}`;

interface Vehicle {
  id: number;
  plateNumber: string;
  vehicleMake?: string;
  vehicleModel?: string;
  year?: number;
  color?: string;
  isActive: boolean;
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [vehicles, setVehicles]   = useState<Vehicle[]>([]);
  const [copiedId, setCopiedId]   = useState<number | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);

  const copyLink = (vehicle: Vehicle) => {
    navigator.clipboard.writeText(vehicleLink(vehicle.plateNumber));
    setCopiedId(vehicle.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  useEffect(() => { fetchVehicles(); }, []);

  const fetchVehicles = async () => {
    try {
      const response = await vehicleAPI.getAllVehicles();
      setVehicles(response.data);
    } catch {
      setError('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const activeVehicles   = vehicles.filter(v =>  v.isActive).length;
  const inactiveVehicles = vehicles.filter(v => !v.isActive).length;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="page-header">
        <h2>Dashboard</h2>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Stat Cards */}
      <div className="row g-3 mb-4">
        {[
          {
            icon: <FiTruck />, iconClass: 'stat-icon-blue',
            value: vehicles.length, label: 'Total Vehicles',
          },
          {
            icon: <FiCheckCircle />, iconClass: 'stat-icon-green',
            value: activeVehicles, label: 'Active Vehicles',
          },
          {
            icon: <FiPauseCircle />, iconClass: 'stat-icon-slate',
            value: inactiveVehicles, label: 'Inactive Vehicles',
          },
        ].map((s, i) => (
          <div className="col-md-4" key={i}>
            <div className="stat-card">
              <div className={`stat-icon ${s.iconClass}`}>{s.icon}</div>
              <div className="stat-body">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="section-card mb-4">
        <div className="section-card-header">
          <span className="section-card-title">
            <FiList style={{ color: '#16a34a' }} /> Quick Actions
          </span>
        </div>
        <div className="section-card-body d-flex flex-wrap gap-2">
          <Link to="/admin/vehicles/new">
            <Button variant="primary" className="d-flex align-items-center gap-2">
              <FiPlus /> Add New Vehicle
            </Button>
          </Link>
          <Link to="/admin/vehicles">
            <Button variant="outline-primary" className="d-flex align-items-center gap-2">
              <FiList /> View All Vehicles
            </Button>
          </Link>
          <Link to="/admin/bulk-import">
            <Button variant="outline-secondary" className="d-flex align-items-center gap-2">
              <FiUploadCloud /> Bulk Import
            </Button>
          </Link>
          {isAdmin && (
            <Link to="/admin/users">
              <Button variant="outline-secondary" className="d-flex align-items-center gap-2">
                <FiUsers /> Manage Users
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Recent Vehicles */}
      <div className="section-card">
        <div className="section-card-header">
          <span className="section-card-title">
            <FiTruck style={{ color: '#16a34a' }} /> Recent Vehicles
          </span>
          <Link to="/admin/vehicles">
            <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.82rem' }}>
              View All →
            </Button>
          </Link>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table mb-0">
            <thead>
              <tr>
                <th>Plate Number</th>
                <th>Make / Model</th>
                <th>Year</th>
                <th>Color</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.slice(0, 10).map((vehicle) => (
                <tr key={vehicle.id}>
                  <td><strong style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>{vehicle.plateNumber}</strong></td>
                  <td style={{ color: '#374151' }}>{vehicle.vehicleMake} {vehicle.vehicleModel}</td>
                  <td>{vehicle.year || <span className="text-muted">—</span>}</td>
                  <td>{vehicle.color || <span className="text-muted">—</span>}</td>
                  <td>
                    {vehicle.isActive
                      ? <Badge bg="success">Active</Badge>
                      : <Badge bg="secondary">Inactive</Badge>}
                  </td>
                  <td>
                    <div className="action-btn-group">
                      <Button
                        variant="outline-secondary" size="sm"
                        onClick={() => copyLink(vehicle)}
                        title={vehicleLink(vehicle.plateNumber)}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiCopy size={12} />
                        {copiedId === vehicle.id ? '✓' : 'Copy'}
                      </Button>
                      <Button
                        variant="outline-success" size="sm"
                        onClick={() => window.open(vehicleLink(vehicle.plateNumber), '_blank')}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiExternalLink size={12} /> Open
                      </Button>
                      <Link to={`/admin/vehicles/${vehicle.id}`}>
                        <Button variant="outline-primary" size="sm" className="d-flex align-items-center gap-1">
                          <FiEdit2 size={12} /> Edit
                        </Button>
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {vehicles.length === 0 && (
            <div className="text-center py-5 text-muted">
              <FiTruck size={32} className="mb-2 d-block mx-auto" style={{ opacity: 0.3 }} />
              No vehicles yet. <Link to="/admin/vehicles/new">Add your first vehicle</Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
